import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Modal, Button, Form, Spinner, Alert, Badge } from "react-bootstrap";
import axios from "axios";
import Hoot from "../../Hoot";
import ClickAllRefreshButtons from "../../ClickAllRefreshButtons";
import { toast } from "react-hot-toast";
import {
  BsGlobe,
  BsFileEarmarkText,
  BsListNested,
  BsPlusLg,
  BsPencil,
  BsTrash,
  BsEye,
  BsGripVertical,
  BsChevronDown,
  BsChevronRight,
  BsArrowLeft,
  BsArrowUp,
  BsArrowDown,
  BsToggleOn,
  BsLayoutSplit,
  BsFiles,
  BsPauseCircle,
  BsPlayCircle,
  BsCollection,
  BsLayoutTextWindow,
  BsSearch,
  BsCheckCircle,
  BsXCircle,
  BsExclamationTriangle,
} from "react-icons/bs";

axios.defaults.withCredentials = true;

/* =========================================================
   CONSTANTES
========================================================= */
const SECTION_TYPES = [
  { value: "hero", label: "Hero Banner" },
  { value: "features", label: "Features" },
  { value: "testimonials", label: "Testemunhos" },
  { value: "gallery", label: "Galeria" },
  { value: "cta", label: "Chamada para ação" },
  { value: "pricing", label: "Preços" },
  { value: "faq", label: "Perguntas frequentes" },
  { value: "footer", label: "Footer" },
  { value: "custom", label: "Personalizado (HTML livre)" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "Todos os estados" },
  { value: "ativo", label: "Ativas" },
  { value: "inativo", label: "Inativas" },
];

/* =========================================================
   HELPERS
========================================================= */
function slugify(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

// Ordena secções por display_order e, em caso de empate, por id.
const sortSections = (a, b) =>
  (a.ed_section_display_order ?? 0) - (b.ed_section_display_order ?? 0) ||
  a.ed_section_id - b.ed_section_id;

const VOID_TAGS = new Set([
  "area", "base", "br", "col", "embed", "hr", "img", "input",
  "link", "meta", "param", "source", "track", "wbr",
]);

/**
 * Validação básica de HTML: verifica emparelhamento grosseiro de tags.
 * Não substitui um parser real, apenas apanha erros comuns (tag aberta
 * sem fechar, tag a fechar sem abrir).
 */
function validateHtmlBasic(html) {
  const warnings = [];
  if (!html || !html.trim()) {
    return { valid: true, warnings };
  }

  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9-]*)\b[^>]*?(\/?)>/g;
  const stack = [];
  let match;

  while ((match = tagRegex.exec(html)) !== null) {
    const [full, tagNameRaw, selfClosingSlash] = match;
    const tagName = tagNameRaw.toLowerCase();
    const isClosing = full.startsWith("</");
    const isSelfClosing = selfClosingSlash === "/" || VOID_TAGS.has(tagName);

    if (isSelfClosing) continue;

    if (isClosing) {
      if (stack.length === 0 || stack[stack.length - 1] !== tagName) {
        warnings.push(`Possível tag "</${tagName}>" sem abertura correspondente.`);
      } else {
        stack.pop();
      }
    } else {
      stack.push(tagName);
    }
  }

  if (stack.length > 0) {
    warnings.push(
      `Possíveis tags por fechar: ${[...new Set(stack)].map((t) => `<${t}>`).join(", ")}.`
    );
  }

  return { valid: warnings.length === 0, warnings };
}

/**
 * Extrai uma mensagem de erro legível e específica a partir de um erro do axios,
 * em vez de mostrar sempre um genérico "Erro ao carregar". Cobre 3 casos:
 * 1) O servidor respondeu com um erro (endpoint existe mas falhou) — mostra a
 *    mensagem do servidor e o código HTTP.
 * 2) O pedido foi feito mas não houve resposta (endpoint não existe, CORS,
 *    servidor em baixo, sem rede) — diz isso explicitamente.
 * 3) Erro a montar o próprio pedido.
 */
function extractErrorMessage(err) {
  if (!err) return "Erro desconhecido.";
  const status = err?.response?.status;
  const serverMsg =
    err?.response?.data?.msg ||
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    (typeof err?.response?.data === "string" ? err.response.data : null);

  if (serverMsg) {
    return `${serverMsg}${status ? ` (HTTP ${status})` : ""}`;
  }
  if (status) {
    return `O servidor respondeu com o código ${status}, sem detalhes adicionais.`;
  }
  if (err?.request && !err?.response) {
    return "Sem resposta do servidor — verifique se o endpoint existe, se o servidor está no ar, ou se há um problema de rede/CORS.";
  }
  if (err?.message) return err.message;
  return "Erro desconhecido ao comunicar com o servidor.";
}

/* =========================================================
   ESTILOS
========================================================= */
const styles = `
.pb-trigger-btn{
  background:#16a34a !important;
  border-color:#16a34a !important;
  border-radius: 999px !important;
  font-weight:700 !important;
  padding: .55rem 1.15rem !important;
  display:inline-flex;
  align-items:center;
  gap:8px;
  box-shadow: 0 8px 20px rgba(22,163,74,.25);
}
.pb-trigger-btn:hover,
.pb-trigger-btn:focus{
  background:#15803d !important;
  border-color:#15803d !important;
}
.pb-trigger-btn svg{
  font-size: 1rem;
}

.pb-main-modal .modal-dialog{
  max-width: 1180px;
}
@media (max-width: 1220px){
  .pb-main-modal .modal-dialog{ max-width: 96vw; }
}
.pb-main-modal .modal-content{
  border-radius: 18px;
  border: 1px solid rgba(226,232,240,.95);
  box-shadow: 0 30px 90px rgba(2,6,23,.22);
  overflow: hidden;
}
.pb-main-modal .modal-header{
  border-bottom: 1px solid rgba(226,232,240,.9);
  background:
    radial-gradient(1000px 260px at 20% 0%, rgba(91,92,255,.16), transparent 60%),
    radial-gradient(900px 240px at 95% 0%, rgba(124,58,237,.12), transparent 55%),
    #ffffff;
  padding: 14px 18px;
}
.pb-main-modal .modal-title{
  font-weight: 650;
  color: #0f172a;
  font-size: 1rem;
}
.pb-main-modal .modal-footer{
  border-top: 1px solid rgba(226,232,240,.9);
  padding: 12px 18px;
  background: #ffffff;
}
.pb-header-left{
  display:flex;
  align-items:center;
  gap:12px;
}
.pb-avatar-wrap{
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(22,163,74,.10);
  border: 1px solid rgba(22,163,74,.18);
  display:grid;
  place-items:center;
}
.pb-title-sub{
  margin-top:2px;
  font-size: .8rem;
  color: #64748b;
  font-weight: 500;
}

/* ----- Shell: sidebar + painel, dentro do único modal ----- */
.pb-shell{
  display:flex;
  min-height: 62vh;
  max-height: 76vh;
}
.pb-sidebar{
  width: 208px;
  flex-shrink:0;
  background: rgba(248,250,252,.75);
  border-right: 1px solid rgba(226,232,240,.9);
  padding: 14px 10px;
  display:flex;
  flex-direction:column;
  gap:4px;
}
.pb-sidebar-item{
  display:flex;
  align-items:center;
  gap:9px;
  border:none;
  background:transparent;
  border-radius: 12px;
  padding: 10px 12px;
  font-size:.88rem;
  font-weight:650;
  color:#64748b;
  text-align:left;
  transition: background .15s ease, color .15s ease;
}
.pb-sidebar-item:hover{ background: rgba(226,232,240,.55); color:#334155; }
.pb-sidebar-item.active{ background: rgba(91,92,255,.1); color:#5b5cff; }
.pb-sidebar-item svg{ font-size: 1rem; width: 18px; flex-shrink:0; }

.pb-panel{
  flex:1;
  min-width:0;
  padding: 18px 20px 20px;
  overflow-y:auto;
  background: linear-gradient(180deg, rgba(248,250,252,.5), #ffffff 40%);
}
.pb-panel-head{
  display:flex;
  align-items:flex-start;
  gap:10px;
  margin-bottom: 14px;
  flex-wrap:wrap;
}
.pb-panel-title{ font-weight: 700; color:#0f172a; font-size: 1.02rem; }
.pb-panel-sub{ font-size:.8rem; color:#64748b; margin-top:2px; }

/* ----- botão "Voltar" com etiqueta, não só ícone ----- */
.pb-back-btn{
  height:34px;
  flex-shrink:0;
  border-radius: 999px;
  border: 1px solid rgba(226,232,240,.95);
  background:#fff;
  color:#475569;
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding: 0 14px 0 10px;
  font-size:.82rem;
  font-weight:650;
  transition: background .15s ease, color .15s ease, border-color .15s ease;
}
.pb-back-btn:hover{
  background: rgba(91,92,255,.08);
  color:#5b5cff;
  border-color: rgba(91,92,255,.3);
}
.pb-back-btn svg{ font-size: .95rem; }

.pb-panel-footer{
  display:flex;
  justify-content:flex-end;
  gap:10px;
  margin-top:18px;
  padding-top:14px;
  border-top: 1px solid rgba(226,232,240,.9);
}

/* ----- confirmação inline (substitui modal de confirmação) ----- */
.pb-inline-confirm{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex-wrap:wrap;
  background: rgba(185,28,28,.06);
  border: 1px solid rgba(185,28,28,.2);
  border-radius: 14px;
  padding: 10px 14px;
  margin-bottom: 14px;
}
.pb-inline-confirm-text{ display:flex; flex-direction:column; gap:2px; font-size:.85rem; color:#7f1d1d; max-width: 520px; }
.pb-inline-confirm-text strong{ display:flex; align-items:center; gap:6px; }
.pb-inline-confirm-actions{ display:flex; gap:8px; flex-shrink:0; }

.pb-error-banner{ font-size:.85rem; }
.pb-error-banner-title{ display:flex; align-items:center; gap:6px; font-weight:700; }

.pb-inline-preview{ margin-bottom: 14px; }

.pb-toolbar{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
}
.pb-search-wrap{
  position: relative;
  min-width: 240px;
}
.pb-search-icon{
  position:absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color:#94a3b8;
  font-size:.85rem;
  pointer-events:none;
}
.pb-search{
  width:100%;
  border-radius: 999px !important;
  border: 1px solid rgba(226,232,240,.95) !important;
  padding: .5rem 1rem .5rem 2.2rem !important;
}
.pb-filter{
  border-radius: 999px !important;
  border: 1px solid rgba(226,232,240,.95) !important;
  min-width: 160px;
}
.pb-new-btn{
  border-radius: 999px !important;
  font-weight: 700 !important;
  white-space: nowrap;
  display:inline-flex !important;
  align-items:center;
  gap:6px;
}

.pb-cards{
  display:grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 14px;
}
.pb-page-card{
  background:#fff;
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 16px;
  padding: 16px 16px 14px;
  display:flex;
  flex-direction:column;
  gap:12px;
  transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease;
}
.pb-page-card:hover{
  box-shadow: 0 14px 32px rgba(2,6,23,.08);
  transform: translateY(-2px);
  border-color: rgba(91,92,255,.25);
}
.pb-page-card-top{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:8px;
}
.pb-page-icon{
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(91,92,255,.1);
  color:#5b5cff;
  display:grid;
  place-items:center;
  font-size: 1.05rem;
  flex-shrink:0;
}
.pb-page-name{
  font-weight: 700;
  color:#0f172a;
  font-size: .98rem;
  line-height:1.3;
}
.pb-page-slug{
  font-size: .76rem;
  color:#5b5cff;
  font-weight: 600;
  background: rgba(91,92,255,.08);
  border-radius: 999px;
  padding: 1px 10px;
  display:inline-block;
  margin-top: 4px;
}
.pb-page-meta{
  font-size: .74rem;
  color:#94a3b8;
  display:flex;
  flex-direction:column;
  gap:2px;
}
.pb-page-actions{
  display:flex;
  gap:6px;
  flex-wrap:wrap;
  margin-top: auto;
  padding-top: 6px;
  border-top: 1px dashed rgba(226,232,240,.95);
}
.pb-icon-btn{
  border-radius: 10px !important;
  font-size:.78rem !important;
  padding: 4px 11px !important;
  display:inline-flex !important;
  align-items:center;
  gap:6px;
  font-weight:600 !important;
}
.pb-empty{
  text-align:center;
  padding: 48px 16px;
  color:#64748b;
  border: 1px dashed rgba(226,232,240,.95);
  border-radius: 16px;
  background: rgba(248,250,252,.6);
}

.pb-cardlet{
  background: rgba(255,255,255,.9);
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 16px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
.pb-cardlet-title{
  font-size: .78rem;
  color:#64748b;
  font-weight: 600;
  display:flex;
  align-items:center;
  gap:8px;
  margin-bottom: 8px;
}
.pb-req{ color:#ef4444; font-weight:700; }
.pb-helper{ font-size: .74rem; color:#64748b; margin-top: 6px; }

.form-label{
  font-size: .78rem !important;
  color: #475569 !important;
  font-weight: 600 !important;
  margin-bottom: 6px !important;
}
.form-control, .form-select{
  border-radius: 12px !important;
  border-color: rgba(226,232,240,.95) !important;
  padding-top: .55rem !important;
  padding-bottom: .55rem !important;
}
.form-control:focus, .form-select:focus{
  box-shadow: 0 0 0 .2rem rgba(91,92,255,.15) !important;
  border-color: rgba(91,92,255,.45) !important;
}

/* secções */
.pb-sections-list{
  display:flex;
  flex-direction:column;
  gap:8px;
}
.pb-section-row{
  display:flex;
  align-items:center;
  gap:10px;
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 14px;
  padding: 8px 10px;
  background:#fff;
  cursor: default;
}
.pb-section-row.dragging{
  opacity:.5;
  border-style:dashed;
}
.pb-section-row.drag-over{
  border-color: rgba(91,92,255,.55);
  background: rgba(91,92,255,.06);
}
.pb-drag-handle{
  cursor: grab;
  color:#94a3b8;
  font-size: 1rem;
  padding: 2px 4px;
  user-select:none;
  display:flex;
  align-items:center;
}
.pb-drag-handle:active{ cursor: grabbing; }
.pb-section-info{
  flex: 1;
  min-width: 0;
}
.pb-section-name{
  font-weight: 650;
  color:#0f172a;
  font-size:.9rem;
  display:flex;
  align-items:center;
  gap:8px;
}
.pb-section-sub{
  font-size:.74rem;
  color:#94a3b8;
  margin-top:2px;
}
.pb-section-actions{
  display:flex;
  gap:4px;
  flex-wrap:wrap;
  align-items:center;
}
.pb-move-group{
  display:flex;
  gap:2px;
  margin-right:4px;
  padding-right:6px;
  border-right:1px solid rgba(226,232,240,.95);
}
.pb-order-badge{
  min-width: 26px;
  height: 26px;
  border-radius: 8px;
  background: rgba(91,92,255,.1);
  color:#5b5cff;
  font-weight:700;
  font-size:.78rem;
  display:grid;
  place-items:center;
}
.pb-empty-sections{
  text-align:center;
  padding: 28px 12px;
  color:#64748b;
  border: 1px dashed rgba(226,232,240,.95);
  border-radius: 14px;
}

.pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  border-radius: 999px;
  padding: 2px 10px;
  border: 1px solid rgba(226,232,240,.95);
  background: rgba(248,250,252,.9);
  font-size: .74rem;
  color:#475569;
  font-weight: 600;
}
.pill.active{ color:#15803d; border-color: rgba(21,128,61,.25); background: rgba(21,128,61,.06); }
.pill.inactive{ color:#b91c1c; border-color: rgba(185,28,28,.25); background: rgba(185,28,28,.06); }

/* editor html */
.pb-editor-grid{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 860px){
  .pb-editor-grid{ grid-template-columns: 1fr; }
}
.pb-editor-textarea{
  width:100%;
  min-height: 320px;
  border-radius: 14px;
  border: 1px solid rgba(226,232,240,.95);
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: .8rem;
  padding: 10px 12px;
  resize: vertical;
  background: #0f172a;
  color: #e2e8f0;
  line-height: 1.5;
}
.pb-editor-textarea:focus{
  outline:none;
  box-shadow: 0 0 0 .2rem rgba(91,92,255,.25);
}
.pb-preview-frame-wrap{
  border-radius: 14px;
  border: 1px solid rgba(226,232,240,.95);
  overflow:hidden;
  min-height: 320px;
  background:#fff;
}
.pb-preview-frame{
  width:100%;
  height:100%;
  min-height: 320px;
  border:0;
}
.pb-editor-toolbar{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
  margin-bottom: 6px;
  flex-wrap:wrap;
}
.pb-editor-toolbar .label{
  font-size:.78rem;
  color:#64748b;
  font-weight:600;
}
.btn-soft{
  border-radius: 999px !important;
  border: 1px solid rgba(226,232,240,.95) !important;
  background: #fff !important;
  display:inline-flex !important;
  align-items:center;
  gap:6px;
}
.btn-primary{ border-radius: 999px !important; }

/* menus de navegação */
.pb-menus-list{
  display:flex;
  flex-direction:column;
  gap:10px;
}
.pb-menu-block{
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 16px;
  background:#fff;
  overflow:hidden;
}
.pb-menu-block.dragging{ opacity:.5; border-style:dashed; }
.pb-menu-block.drag-over{ border-color: rgba(91,92,255,.55); background: rgba(91,92,255,.04); }
.pb-menu-row{
  display:flex;
  align-items:center;
  gap:10px;
  padding: 10px 12px;
}
.pb-menu-toggle{
  border:0;
  background:transparent;
  color:#94a3b8;
  font-size: 1rem;
  padding: 2px 4px;
  display:flex;
  align-items:center;
}
.pb-menu-name{
  font-weight:700;
  color:#0f172a;
  font-size:.92rem;
  display:flex;
  align-items:center;
  gap:8px;
}
.pb-menu-link{
  font-size:.76rem;
  color:#5b5cff;
  margin-top:2px;
}
.pb-menu-no-link{
  font-size:.74rem;
  color:#94a3b8;
  margin-top:2px;
  font-style: italic;
}
.pb-submenus-wrap{
  border-top: 1px solid rgba(226,232,240,.95);
  background: rgba(248,250,252,.6);
  padding: 10px 12px 12px 44px;
}
.pb-submenu-row{
  display:flex;
  align-items:center;
  gap:10px;
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 12px;
  background:#fff;
  padding: 6px 10px;
  margin-bottom: 6px;
}
.pb-submenu-row.dragging{ opacity:.5; border-style:dashed; }
.pb-submenu-row.drag-over{ border-color: rgba(91,92,255,.55); background: rgba(91,92,255,.06); }
.pb-submenu-name{ font-weight:650; color:#0f172a; font-size:.86rem; }
.pb-submenu-link{ font-size:.74rem; color:#5b5cff; }
.pb-add-submenu-btn{
  border-radius: 999px !important;
  font-size:.78rem !important;
  padding: 3px 12px !important;
  display:inline-flex !important;
  align-items:center;
  gap:4px;
}

@media (max-width: 760px){
  .pb-shell{ flex-direction:column; max-height:none; }
  .pb-sidebar{ width:100%; flex-direction:row; border-right:0; border-bottom:1px solid rgba(226,232,240,.9); overflow-x:auto; }
  .pb-sidebar-item{ white-space:nowrap; }
}
`;

/* =========================================================
   COMPONENTE: botão "Voltar" reutilizável (ícone + etiqueta)
========================================================= */
function BackButton({ onClick, label = "Voltar" }) {
  return (
    <button type="button" className="pb-back-btn" onClick={onClick}>
      <BsArrowLeft />
      <span>{label}</span>
    </button>
  );
}

/* =========================================================
   COMPONENTE: confirmação inline (substitui o antigo ConfirmModal)
========================================================= */
function InlineConfirm({ show, title, message, confirmLabel = "Eliminar", danger = true, loading, onConfirm, onCancel }) {
  if (!show) return null;
  return (
    <div className="pb-inline-confirm">
      <div className="pb-inline-confirm-text">
        <strong>
          <BsExclamationTriangle /> {title}
        </strong>
        <span>{message}</span>
      </div>
      <div className="pb-inline-confirm-actions">
        <Button size="sm" variant="outline-secondary" className="btn-soft" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button size="sm" variant={danger ? "danger" : "primary"} onClick={onConfirm} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/* =========================================================
   COMPONENTE: banner de erro com detalhe real + tentar novamente
========================================================= */
function ErrorBanner({ error, onRetry }) {
  if (!error) return null;
  return (
    <Alert variant="danger" className="mb-3 pb-error-banner" style={{ borderRadius: 14 }}>
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
        <div>
          <div className="pb-error-banner-title">
            <BsExclamationTriangle /> Não foi possível carregar
          </div>
          <div>{error}</div>
        </div>
        {onRetry && (
          <Button size="sm" variant="outline-danger" onClick={onRetry}>
            Tentar novamente
          </Button>
        )}
      </div>
    </Alert>
  );
}

/* =========================================================
   PAINEL: Lista de páginas
========================================================= */
function PagesListPanel({ pages, loading, error, onRetry, onCreate, onEdit, onSections, onReload }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filteredPages = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (pages || []).filter((p) => {
      const matchesTerm =
        !term ||
        p.ed_page_name?.toLowerCase().includes(term) ||
        p.ed_page_slug?.toLowerCase().includes(term) ||
        p.ed_page_title?.toLowerCase().includes(term);
      const matchesStatus = statusFilter === "all" || p.ed_page_status === statusFilter;
      return matchesTerm && matchesStatus;
    });
  }, [pages, search, statusFilter]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(Hoot() + "pages/" + pendingDelete.ed_page_id, { withCredentials: true });
      toast.success("Página eliminada com sucesso!");
      ClickAllRefreshButtons();
      setPendingDelete(null);
      onReload();
    } catch (err) {
      console.error("Erro ao eliminar página:", err);
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="pb-panel-head">
        <div>
          <div className="pb-panel-title">Páginas</div>
          <div className="pb-panel-sub">Cria, edita e organiza as páginas do website.</div>
        </div>
      </div>

      <div className="pb-toolbar mb-3">
        <div className="pb-search-wrap">
          <BsSearch className="pb-search-icon" />
          <Form.Control
            className="pb-search"
            type="text"
            placeholder="Pesquisar por nome, slug ou título..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Form.Select className="pb-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
        <Button variant="primary" className="pb-new-btn ms-auto" onClick={onCreate}>
          <BsPlusLg /> Nova página
        </Button>
      </div>

      <ErrorBanner error={error} onRetry={onRetry} />

      <InlineConfirm
        show={!!pendingDelete}
        title="Eliminar página"
        message={`Tem a certeza que pretende eliminar "${pendingDelete?.ed_page_title}"? Todas as secções associadas também serão eliminadas. Esta ação não pode ser revertida.`}
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : filteredPages.length === 0 ? (
        <div className="pb-empty">
          {error
            ? "Não foi possível mostrar as páginas — ver detalhe do erro acima."
            : pages.length === 0
            ? "Ainda não existem páginas. Cria a primeira para começar."
            : "Nenhuma página corresponde à pesquisa/filtro atual."}
        </div>
      ) : (
        <div className="pb-cards">
          {filteredPages.map((page) => (
            <div className="pb-page-card" key={page.ed_page_id}>
              <div className="pb-page-card-top">
                <div className="pb-page-icon">
                  <BsFileEarmarkText />
                </div>
                <span className={`pill ${page.ed_page_status === "ativo" ? "active" : "inactive"}`}>
                  {page.ed_page_status === "ativo" ? (
                    <>
                      <BsCheckCircle /> Ativa
                    </>
                  ) : (
                    <>
                      <BsXCircle /> Inativa
                    </>
                  )}
                </span>
              </div>

              <div>
                <div className="pb-page-name">{page.ed_page_title}</div>
                <span className="pb-page-slug">/{page.ed_page_slug}</span>
              </div>

              <div className="pb-page-meta">
                <span>Nome interno: {page.ed_page_name}</span>
                <span>Criada em {formatDate(page.ed_page_created_at)}</span>
                <span>Atualizada em {formatDate(page.ed_page_updated_at)}</span>
              </div>

              <div className="pb-page-actions">
                <Button size="sm" variant="outline-primary" className="pb-icon-btn" onClick={() => onSections(page)}>
                  <BsLayoutSplit /> Secções
                </Button>
                <Button size="sm" variant="outline-secondary" className="pb-icon-btn" onClick={() => onEdit(page)}>
                  <BsPencil /> Editar
                </Button>
                <Button size="sm" variant="outline-danger" className="pb-icon-btn" onClick={() => setPendingDelete(page)}>
                  <BsTrash /> Eliminar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   PAINEL: Criar/editar página
========================================================= */
function PageFormPanel({ page, existingPages, onBack, onSaved }) {
  const isEdit = !!page;
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("ativo");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setName(page?.ed_page_name || "");
    setSlug(page?.ed_page_slug || "");
    setSlugTouched(!!page?.ed_page_slug);
    setTitle(page?.ed_page_title || "");
    setStatus(page?.ed_page_status || "ativo");
    setError(null);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  const slugTaken = useMemo(() => {
    const s = slugify(slug);
    if (!s) return false;
    return (existingPages || []).some(
      (p) => p.ed_page_slug === s && (!isEdit || p.ed_page_id !== page?.ed_page_id)
    );
  }, [slug, existingPages, isEdit, page]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const finalSlug = slugify(slug);
    if (!name.trim() || !finalSlug || !title.trim()) {
      toast.error("Preencha todos os campos obrigatórios!");
      return;
    }
    if (slugTaken) {
      setError("Já existe uma página com este nome interno (slug). Escolha outro.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = { name: name.trim(), slug: finalSlug, title: title.trim(), status };

      if (isEdit) {
        await axios.put(Hoot() + "pages/" + page.ed_page_id, payload, { withCredentials: true });
        toast.success("Página atualizada com sucesso!");
      } else {
        await axios.post(Hoot() + "pages", payload, { withCredentials: true });
        toast.success("Página criada com sucesso!");
      }

      ClickAllRefreshButtons();
      onSaved();
    } catch (err) {
      console.error("Erro ao guardar página:", err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="pb-panel-head">
        <BackButton onClick={onBack} />
        <div>
          <div className="pb-panel-title">{isEdit ? "Editar página" : "Nova página"}</div>
          <div className="pb-panel-sub">{isEdit ? page?.ed_page_title : "Preenche os campos para criar uma nova página."}</div>
        </div>
      </div>

      <ErrorBanner error={error} />

      <Form id="page-form" onSubmit={handleSubmit}>
        <div className="pb-cardlet">
          <div className="pb-cardlet-title">
            <BsFileEarmarkText />
            Identificação
          </div>

          <Form.Group className="mb-3">
            <Form.Label>
              Nome interno <span className="pb-req">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Página inicial"
              required
            />
            <div className="pb-helper">Usado apenas no painel administrativo.</div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Slug (identificador único) <span className="pb-req">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="ex-pagina-inicial"
              required
              isInvalid={slugTaken}
            />
            <Form.Control.Feedback type="invalid">
              Este slug já está a ser utilizado.
            </Form.Control.Feedback>
            <div className="pb-helper">Usado para gerar o URL público da página. Não pode repetir-se.</div>
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label>
              Título público <span className="pb-req">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título visível no website"
              required
            />
          </Form.Group>
        </div>

        <div className="pb-cardlet">
          <div className="pb-cardlet-title">
            <BsToggleOn />
            Estado
          </div>
          <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ativo">Ativa</option>
            <option value="inativo">Inativa</option>
          </Form.Select>
          <div className="pb-helper">Páginas inativas não são renderizadas no website público.</div>
        </div>
      </Form>

      <div className="pb-panel-footer">
        <Button variant="outline-secondary" className="btn-soft" onClick={onBack} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" form="page-form" variant="primary" disabled={loading || slugTaken}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              A guardar...
            </>
          ) : isEdit ? (
            "Guardar alterações"
          ) : (
            "Criar página"
          )}
        </Button>
      </div>
    </div>
  );
}

/* =========================================================
   PAINEL: Secções de uma página (com preview inline, sem modal)
   - Setas subir/descer + drag & drop (só pela alça)
   - Ignora respostas desatualizadas e filtra por página
========================================================= */
function PageSectionsPanel({ page, onBack, onCreateSection, onEditSection }) {
  const pageId = page?.ed_page_id;

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showPreview, setShowPreview] = useState(false);

  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  // Evita que uma resposta antiga (de outra página) sobrescreva a lista atual
  const requestRef = useRef(0);

  const loadSections = useCallback(async () => {
    if (!pageId) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(Hoot() + "pages/" + pageId + "/sections", {
        withCredentials: true,
        params: { _t: Date.now() }, // evita cache do browser
      });
      if (requestId !== requestRef.current) return; // resposta desatualizada

      const list = (Array.isArray(res.data) ? res.data : [])
        // defesa extra: só secções desta página
        .filter((s) => String(s.ed_section_page_id) === String(pageId))
        .sort(sortSections);
      setSections(list);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error("Erro ao carregar secções:", err);
      setError(extractErrorMessage(err));
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    setSections([]); // limpa ao mudar de página
    loadSections();
  }, [loadSections]);

  const handleToggleStatus = async (section) => {
    const nextStatus = section.ed_section_status === "ativo" ? "inativo" : "ativo";
    try {
      await axios.put(
        Hoot() + "sections/" + section.ed_section_id,
        { status: nextStatus },
        { withCredentials: true }
      );
      toast.success(nextStatus === "ativo" ? "Secção ativada." : "Secção desativada.");
      ClickAllRefreshButtons();
      loadSections();
    } catch (err) {
      console.error("Erro ao alterar o estado da secção:", err);
      toast.error(extractErrorMessage(err));
    }
  };

  const handleDuplicate = async (section) => {
    try {
      await axios.post(Hoot() + "sections/" + section.ed_section_id + "/duplicate", {}, { withCredentials: true });
      toast.success("Secção duplicada com sucesso!");
      ClickAllRefreshButtons();
      loadSections();
    } catch (err) {
      console.error("Erro ao duplicar a secção:", err);
      toast.error(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(Hoot() + "sections/" + pendingDelete.ed_section_id, { withCredentials: true });
      toast.success("Secção eliminada com sucesso!");
      ClickAllRefreshButtons();
      setPendingDelete(null);
      loadSections();
    } catch (err) {
      console.error("Erro ao eliminar a secção:", err);
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  /* --- Gravar nova ordem (usado por drag & drop E pelas setas) --- */
  const persistOrder = async (reordered) => {
    const previous = sections;
    const withNewOrder = reordered.map((s, i) => ({ ...s, ed_section_display_order: i }));

    setSections(withNewOrder); // atualização otimista
    setSavingOrder(true);
    try {
      await axios.put(
        Hoot() + "sections/reorder",
        {
          page_id: pageId,
          order: withNewOrder.map((s) => ({ id: s.ed_section_id, display_order: s.ed_section_display_order })),
        },
        { withCredentials: true }
      );
      ClickAllRefreshButtons();
    } catch (err) {
      console.error("Erro ao guardar a nova ordem das secções:", err);
      setSections(previous); // reverte
      toast.error(extractErrorMessage(err));
    } finally {
      setSavingOrder(false);
    }
  };

  /* --- Setas subir / descer --- */
  const moveSection = (idx, direction) => {
    const target = idx + direction;
    if (savingOrder || target < 0 || target >= sections.length) return;
    const reordered = [...sections];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    persistOrder(reordered);
  };

  /* --- Drag & drop (HTML5) --- */
  const handleDragStart = (e, idx) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx)); // necessário no Firefox
    setDragIndex(idx);
  };
  const handleDragOver = (e, idx) => {
    if (dragIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== dragOverIndex) setDragOverIndex(idx);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handleDrop = (e, idx) => {
    e.preventDefault();
    const from = dragIndex;
    setDragOverIndex(null);
    setDragIndex(null);
    if (from === null || from === idx || savingOrder) return;

    const reordered = [...sections];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(idx, 0, moved);
    persistOrder(reordered);
  };

  const previewHtml = useMemo(() => {
    const active = sections.filter((s) => s.ed_section_status === "ativo");
    const body = active.map((s) => s.ed_section_html_content || "").join("\n");
    return `<!doctype html>
<html lang="pt">
<head>
<meta charset="utf-8" />
<title>${page?.ed_page_title || "Pré-visualização"}</title>
<meta name="viewport" content="width=device-width, initial-scale=1" />
</head>
<body>${body}</body>
</html>`;
  }, [sections, page]);

  return (
    <div>
      <div className="pb-panel-head">
        <BackButton onClick={onBack} />
        <div className="flex-grow-1">
          <div className="pb-panel-title">Secções — {page?.ed_page_title}</div>
          <div className="pb-panel-sub">
            Use as setas ou arraste pela alça para reordenar. A ordem é aplicada na renderização pública.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" className="btn-soft" size="sm" onClick={() => setShowPreview((v) => !v)}>
            <BsEye /> {showPreview ? "Ocultar preview" : "Preview"}
          </Button>
          <Button variant="primary" size="sm" className="pb-new-btn" onClick={onCreateSection}>
            <BsPlusLg /> Nova secção
          </Button>
        </div>
      </div>

      <ErrorBanner error={error} onRetry={loadSections} />

      <InlineConfirm
        show={!!pendingDelete}
        title="Eliminar secção"
        message={`Tem a certeza que pretende eliminar a secção "${pendingDelete?.ed_section_name}"? Esta ação não pode ser revertida.`}
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {showPreview && (
        <div className="pb-cardlet pb-inline-preview">
          <div className="pb-cardlet-title">
            <BsEye /> Preview da página (secções ativas, em ordem)
          </div>
          <div className="pb-preview-frame-wrap">
            <iframe
              title="preview-pagina"
              className="pb-preview-frame"
              style={{ minHeight: 360 }}
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : sections.length === 0 ? (
        <div className="pb-empty-sections">
          {error
            ? "Não foi possível mostrar as secções — ver detalhe do erro acima."
            : "Esta página ainda não tem secções. Crie a primeira para começar a compor o layout."}
        </div>
      ) : (
        <div className="pb-sections-list">
          {sections.map((section, idx) => (
            <div
              key={section.ed_section_id}
              className={`pb-section-row${dragIndex === idx ? " dragging" : ""}${dragOverIndex === idx ? " drag-over" : ""}`}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragLeave={() => setDragOverIndex((c) => (c === idx ? null : c))}
              onDrop={(e) => handleDrop(e, idx)}
            >
              <span
                className="pb-drag-handle"
                title="Arrastar para reordenar"
                draggable={!savingOrder}
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragEnd={handleDragEnd}
              >
                <BsGripVertical />
              </span>

              <span className="pb-order-badge">{idx + 1}</span>

              <div className="pb-section-info">
                <div className="pb-section-name">
                  {section.ed_section_name}
                  <span className={`pill ${section.ed_section_status === "ativo" ? "active" : "inactive"}`}>
                    {section.ed_section_status === "ativo" ? (
                      <>
                        <BsCheckCircle /> Ativa
                      </>
                    ) : (
                      <>
                        <BsXCircle /> Inativa
                      </>
                    )}
                  </span>
                </div>
                <div className="pb-section-sub">
                  {SECTION_TYPES.find((t) => t.value === section.ed_section_type)?.label || section.ed_section_type}
                  {" · "}
                  Atualizado em {formatDate(section.ed_section_updated_at)}
                </div>
              </div>

              <div className="pb-section-actions">
                <div className="pb-move-group">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="pb-icon-btn"
                    onClick={() => moveSection(idx, -1)}
                    disabled={idx === 0 || savingOrder}
                    title="Mover para cima"
                  >
                    <BsArrowUp />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="pb-icon-btn"
                    onClick={() => moveSection(idx, 1)}
                    disabled={idx === sections.length - 1 || savingOrder}
                    title="Mover para baixo"
                  >
                    <BsArrowDown />
                  </Button>
                </div>

                <Button size="sm" variant="outline-secondary" className="pb-icon-btn" onClick={() => onEditSection(section)} title="Editar">
                  <BsPencil />
                </Button>
                <Button size="sm" variant="outline-secondary" className="pb-icon-btn" onClick={() => handleDuplicate(section)} title="Duplicar">
                  <BsFiles />
                </Button>
                <Button
                  size="sm"
                  variant={section.ed_section_status === "ativo" ? "outline-warning" : "outline-success"}
                  className="pb-icon-btn"
                  onClick={() => handleToggleStatus(section)}
                  title="Ativar/Desativar"
                >
                  {section.ed_section_status === "ativo" ? <BsPauseCircle /> : <BsPlayCircle />}
                </Button>
                <Button size="sm" variant="outline-danger" className="pb-icon-btn" onClick={() => setPendingDelete(section)} title="Eliminar">
                  <BsTrash />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {savingOrder && (
        <div className="pb-helper mt-2">
          <Spinner animation="border" size="sm" className="me-2" />
          A guardar nova ordem...
        </div>
      )}
    </div>
  );
}

/* =========================================================
   PAINEL: Editor de secção (HTML + preview + templates)
========================================================= */
function SectionFormPanel({ page, section, onBack, onSaved }) {
  const isEdit = !!section;
  const [name, setName] = useState("");
  const [type, setType] = useState("custom");
  const [status, setStatus] = useState("ativo");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [error, setError] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  useEffect(() => {
    setName(section?.ed_section_name || "");
    setType(section?.ed_section_type || "custom");
    setStatus(section?.ed_section_status || "ativo");
    setHtml(section?.ed_section_html_content || "");
    setError(null);
    setLoading(false);
    setSelectedTemplateId("");
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  async function loadTemplates() {
    setLoadingTemplates(true);
    setTemplatesError(null);
    try {
      const res = await axios.get(Hoot() + "section-templates", { withCredentials: true });
      setTemplates(res.data || []);
    } catch (err) {
      console.error("Erro ao carregar templates:", err);
      setTemplatesError(extractErrorMessage(err));
    } finally {
      setLoadingTemplates(false);
    }
  }

  const validation = useMemo(() => validateHtmlBasic(html), [html]);

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    const tpl = templates.find((t) => String(t.ed_template_id) === String(templateId));
    if (tpl) {
      setHtml(tpl.ed_template_html_content || "");
      if (!name) setName(tpl.ed_template_name || "");
      if (tpl.ed_template_type) setType(tpl.ed_template_type);
      toast.success("Template carregado. Podes editar livremente antes de guardar.");
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!html.trim()) {
      toast.error("Não há conteúdo HTML para guardar como template.");
      return;
    }
    const templateName = window.prompt("Nome para este template reutilizável:", name || "Novo template");
    if (!templateName) return;

    setSavingTemplate(true);
    try {
      await axios.post(
        Hoot() + "section-templates",
        { name: templateName.trim(), type, html_content: html },
        { withCredentials: true }
      );
      toast.success("Template guardado com sucesso!");
      loadTemplates();
    } catch (err) {
      console.error("Erro ao guardar template:", err);
      toast.error(extractErrorMessage(err));
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!name.trim()) {
      toast.error("Indique um nome para a secção.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = { name: name.trim(), type, status, html_content: html };

      if (isEdit) {
        await axios.put(Hoot() + "sections/" + section.ed_section_id, payload, { withCredentials: true });
        toast.success("Secção atualizada com sucesso!");
      } else {
        await axios.post(Hoot() + "pages/" + page.ed_page_id + "/sections", payload, { withCredentials: true });
        toast.success("Secção criada com sucesso!");
      }

      ClickAllRefreshButtons();
      onSaved();
    } catch (err) {
      console.error("Erro ao guardar secção:", err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="pb-panel-head">
        <BackButton onClick={onBack} />
        <div>
          <div className="pb-panel-title">{isEdit ? "Editar secção" : "Nova secção"}</div>
          <div className="pb-panel-sub">{page?.ed_page_title}</div>
        </div>
      </div>

      <ErrorBanner error={error} />

      <Form id="section-form" onSubmit={handleSubmit}>
        <div className="pb-cardlet">
          <div className="pb-cardlet-title">
            <BsLayoutTextWindow />
            Detalhes da secção
          </div>

          <div className="pb-editor-grid" style={{ marginBottom: 12 }}>
            <Form.Group>
              <Form.Label>
                Nome da secção <span className="pb-req">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex.: Hero Banner"
                required
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Tipo de secção</Form.Label>
              <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
                {SECTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          <Form.Group>
            <Form.Label>Estado</Form.Label>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="ativo">Ativa</option>
              <option value="inativo">Inativa</option>
            </Form.Select>
            <div className="pb-helper">Secções inativas não aparecem na renderização pública.</div>
          </Form.Group>
        </div>

        <div className="pb-cardlet">
          <div className="pb-cardlet-title">
            <BsCollection />
            Templates reutilizáveis
          </div>

          <ErrorBanner error={templatesError} onRetry={loadTemplates} />

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <Form.Select
              style={{ maxWidth: 320 }}
              value={selectedTemplateId}
              onChange={(e) => applyTemplate(e.target.value)}
              disabled={loadingTemplates}
            >
              <option value="">
                {loadingTemplates ? "A carregar templates..." : "-- Carregar template --"}
              </option>
              {templates.map((t) => (
                <option key={t.ed_template_id} value={t.ed_template_id}>
                  {t.ed_template_name}
                </option>
              ))}
            </Form.Select>

            <Button
              type="button"
              variant="outline-secondary"
              className="btn-soft"
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate}
            >
              {savingTemplate ? <Spinner animation="border" size="sm" /> : "Guardar HTML atual como template"}
            </Button>
          </div>
        </div>

        <div className="pb-cardlet">
          <div className="pb-editor-toolbar">
            <span className="label">Editor HTML e preview em tempo real</span>
            {validation.warnings.length > 0 ? (
              <Badge bg="warning" text="dark">
                {validation.warnings.length} aviso(s) de validação
              </Badge>
            ) : (
              <Badge bg="success">HTML sem avisos</Badge>
            )}
          </div>

          <div className="pb-editor-grid">
            <textarea
              className="pb-editor-textarea"
              spellCheck={false}
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              placeholder="<section>...</section>"
            />
            <div className="pb-preview-frame-wrap">
              <iframe
                title="preview-secção"
                className="pb-preview-frame"
                srcDoc={html || "<p style='font-family:sans-serif;color:#94a3b8;padding:16px;'>Sem conteúdo para pré-visualizar.</p>"}
                sandbox="allow-same-origin"
              />
            </div>
          </div>

          {validation.warnings.length > 0 && (
            <Alert variant="warning" className="mt-2 mb-0" style={{ borderRadius: 14 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Validação básica encontrou o seguinte:</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validation.warnings.map((w, i) => (
                  <li key={i} style={{ fontSize: ".82rem" }}>
                    {w}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </div>
      </Form>

      <div className="pb-panel-footer">
        <Button variant="outline-secondary" className="btn-soft" onClick={onBack} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" form="section-form" variant="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              A guardar...
            </>
          ) : isEdit ? (
            "Guardar alterações"
          ) : (
            "Criar secção"
          )}
        </Button>
      </div>
    </div>
  );
}

/* =========================================================
   PAINEL: Lista de menus de navegação (+ submenus)
========================================================= */
function MenusListPanel({ menus, loading, error, onRetry, onReload, onCreateMenu, onEditMenu, onCreateSubmenu, onEditSubmenu }) {
  const [expanded, setExpanded] = useState({});

  const [pendingDeleteMenu, setPendingDeleteMenu] = useState(null);
  const [pendingDeleteSubmenu, setPendingDeleteSubmenu] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [menuDragIndex, setMenuDragIndex] = useState(null);
  const [menuDragOverIndex, setMenuDragOverIndex] = useState(null);

  const [submenuDrag, setSubmenuDrag] = useState({ menuId: null, index: null });
  const [submenuDragOver, setSubmenuDragOver] = useState({ menuId: null, index: null });

  const toggleExpand = (menuId) => {
    setExpanded((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  const handleDeleteMenu = async () => {
    if (!pendingDeleteMenu) return;
    setDeleting(true);
    try {
      await axios.delete(Hoot() + "nav-menus/" + pendingDeleteMenu.ed_menu_id, { withCredentials: true });
      toast.success("Menu eliminado com sucesso!");
      ClickAllRefreshButtons();
      setPendingDeleteMenu(null);
      onReload();
    } catch (err) {
      console.error("Erro ao eliminar menu:", err);
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSubmenu = async () => {
    if (!pendingDeleteSubmenu) return;
    setDeleting(true);
    try {
      await axios.delete(Hoot() + "nav-submenus/" + pendingDeleteSubmenu.ed_submenu_id, {
        withCredentials: true,
      });
      toast.success("Submenu eliminado com sucesso!");
      ClickAllRefreshButtons();
      setPendingDeleteSubmenu(null);
      onReload();
    } catch (err) {
      console.error("Erro ao eliminar submenu:", err);
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  /* --- drag & drop de MENUS --- */
  const handleMenuDrop = async (idx) => {
    setMenuDragOverIndex(null);
    if (menuDragIndex === null || menuDragIndex === idx) {
      setMenuDragIndex(null);
      return;
    }

    const reordered = [...menus];
    const [moved] = reordered.splice(menuDragIndex, 1);
    reordered.splice(idx, 0, moved);
    const withOrder = reordered.map((m, i) => ({ ...m, ed_menu_display_order: i }));
    setMenuDragIndex(null);

    try {
      await axios.put(
        Hoot() + "nav-menus/reorder",
        { order: withOrder.map((m) => ({ id: m.ed_menu_id, display_order: m.ed_menu_display_order })) },
        { withCredentials: true }
      );
      ClickAllRefreshButtons();
      onReload();
    } catch (err) {
      console.error("Erro ao guardar a nova ordem dos menus:", err);
      toast.error(extractErrorMessage(err));
      onReload();
    }
  };

  /* --- drag & drop de SUBMENUS (dentro do mesmo menu) --- */
  const handleSubmenuDrop = async (menu, idx) => {
    setSubmenuDragOver({ menuId: null, index: null });
    if (submenuDrag.menuId !== menu.ed_menu_id || submenuDrag.index === null || submenuDrag.index === idx) {
      setSubmenuDrag({ menuId: null, index: null });
      return;
    }

    const submenus = [...(menu.submenus || [])];
    const [moved] = submenus.splice(submenuDrag.index, 1);
    submenus.splice(idx, 0, moved);
    const withOrder = submenus.map((s, i) => ({ ...s, ed_submenu_display_order: i }));
    setSubmenuDrag({ menuId: null, index: null });

    try {
      await axios.put(
        Hoot() + "nav-submenus/reorder",
        {
          menu_id: menu.ed_menu_id,
          order: withOrder.map((s) => ({ id: s.ed_submenu_id, display_order: s.ed_submenu_display_order })),
        },
        { withCredentials: true }
      );
      ClickAllRefreshButtons();
      onReload();
    } catch (err) {
      console.error("Erro ao guardar a nova ordem dos submenus:", err);
      toast.error(extractErrorMessage(err));
      onReload();
    }
  };

  return (
    <div>
      <div className="pb-panel-head">
        <div>
          <div className="pb-panel-title">Menu de Navegação</div>
          <div className="pb-panel-sub">
            Arraste pela alça para reordenar menus e submenus. Um menu com submenus não tem link próprio.
          </div>
        </div>
        <Button variant="primary" className="pb-new-btn ms-auto" onClick={onCreateMenu}>
          <BsPlusLg /> Novo menu
        </Button>
      </div>

      <ErrorBanner error={error} onRetry={onRetry} />

      <InlineConfirm
        show={!!pendingDeleteMenu}
        title="Eliminar menu"
        message={`Tem a certeza que pretende eliminar o menu "${pendingDeleteMenu?.ed_menu_name}"? Todos os submenus associados também serão eliminados.`}
        loading={deleting}
        onConfirm={handleDeleteMenu}
        onCancel={() => setPendingDeleteMenu(null)}
      />

      <InlineConfirm
        show={!!pendingDeleteSubmenu}
        title="Eliminar submenu"
        message={`Tem a certeza que pretende eliminar o submenu "${pendingDeleteSubmenu?.ed_submenu_name}"?`}
        loading={deleting}
        onConfirm={handleDeleteSubmenu}
        onCancel={() => setPendingDeleteSubmenu(null)}
      />

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : menus.length === 0 ? (
        <div className="pb-empty">
          {error ? "Não foi possível mostrar os menus — ver detalhe do erro acima." : "Ainda não existem menus. Crie o primeiro para montar a navbar."}
        </div>
      ) : (
        <div className="pb-menus-list">
          {menus.map((menu, idx) => {
            const submenus = menu.submenus || [];
            const isOpen = !!expanded[menu.ed_menu_id];

            return (
              <div
                key={menu.ed_menu_id}
                className={`pb-menu-block${menuDragIndex === idx ? " dragging" : ""}${
                  menuDragOverIndex === idx ? " drag-over" : ""
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (menuDragOverIndex !== idx) setMenuDragOverIndex(idx);
                }}
                onDragLeave={() => setMenuDragOverIndex((c) => (c === idx ? null : c))}
                onDrop={() => handleMenuDrop(idx)}
              >
                <div className="pb-menu-row">
                  <span
                    className="pb-drag-handle"
                    draggable
                    onDragStart={() => setMenuDragIndex(idx)}
                    title="Arrastar para reordenar"
                  >
                    <BsGripVertical />
                  </span>

                  <span className="pb-order-badge">{idx + 1}</span>

                  <button type="button" className="pb-menu-toggle" onClick={() => toggleExpand(menu.ed_menu_id)}>
                    {isOpen ? <BsChevronDown /> : <BsChevronRight />}
                  </button>

                  <div className="pb-section-info">
                    <div className="pb-menu-name">
                      {menu.ed_menu_name}
                      {submenus.length > 0 && (
                        <span className="pill">
                          <BsListNested /> {submenus.length} submenu{submenus.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    {submenus.length > 0 ? (
                      <div className="pb-menu-no-link">Sem link próprio (tem submenus).</div>
                    ) : menu.ed_menu_link ? (
                      <div className="pb-menu-link">{menu.ed_menu_link}</div>
                    ) : (
                      <div className="pb-menu-no-link">Sem link definido.</div>
                    )}
                  </div>

                  <div className="pb-section-actions">
                    <Button size="sm" className="pb-add-submenu-btn btn-soft" variant="outline-secondary" onClick={() => onCreateSubmenu(menu)}>
                      <BsPlusLg /> Submenu
                    </Button>
                    <Button size="sm" variant="outline-secondary" className="pb-icon-btn" onClick={() => onEditMenu(menu)} title="Editar">
                      <BsPencil />
                    </Button>
                    <Button size="sm" variant="outline-danger" className="pb-icon-btn" onClick={() => setPendingDeleteMenu(menu)} title="Eliminar">
                      <BsTrash />
                    </Button>
                  </div>
                </div>

                {isOpen && (
                  <div className="pb-submenus-wrap">
                    {submenus.length === 0 ? (
                      <div className="pb-helper">Este menu ainda não tem submenus.</div>
                    ) : (
                      submenus.map((sub, subIdx) => (
                        <div
                          key={sub.ed_submenu_id}
                          className={`pb-submenu-row${
                            submenuDrag.menuId === menu.ed_menu_id && submenuDrag.index === subIdx ? " dragging" : ""
                          }${
                            submenuDragOver.menuId === menu.ed_menu_id && submenuDragOver.index === subIdx
                              ? " drag-over"
                              : ""
                          }`}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setSubmenuDragOver({ menuId: menu.ed_menu_id, index: subIdx });
                          }}
                          onDragLeave={() =>
                            setSubmenuDragOver((c) => (c.menuId === menu.ed_menu_id && c.index === subIdx ? { menuId: null, index: null } : c))
                          }
                          onDrop={() => handleSubmenuDrop(menu, subIdx)}
                        >
                          <span
                            className="pb-drag-handle"
                            draggable
                            onDragStart={() => setSubmenuDrag({ menuId: menu.ed_menu_id, index: subIdx })}
                          >
                            <BsGripVertical />
                          </span>
                          <span className="pb-order-badge">{subIdx + 1}</span>
                          <div className="pb-section-info">
                            <div className="pb-submenu-name">{sub.ed_submenu_name}</div>
                            <div className="pb-submenu-link">{sub.ed_submenu_link}</div>
                          </div>
                          <div className="pb-section-actions">
                            <Button size="sm" variant="outline-secondary" className="pb-icon-btn" onClick={() => onEditSubmenu(menu, sub)} title="Editar">
                              <BsPencil />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="pb-icon-btn"
                              onClick={() => setPendingDeleteSubmenu(sub)}
                              title="Eliminar"
                            >
                              <BsTrash />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   PAINEL: Criar/editar MENU
========================================================= */
function MenuFormPanel({ menu, onBack, onSaved }) {
  const isEdit = !!menu;
  const hasSubmenus = (menu?.submenus || []).length > 0;

  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setName(menu?.ed_menu_name || "");
    setLink(menu?.ed_menu_link || "");
    setError(null);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!name.trim()) {
      toast.error("Indique um nome para o menu.");
      return;
    }
    if (hasSubmenus && link.trim()) {
      toast.error("Este menu tem submenus, por isso não pode ter link próprio.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = { name: name.trim(), link: hasSubmenus ? "" : link.trim() };

      if (isEdit) {
        await axios.put(Hoot() + "nav-menus/" + menu.ed_menu_id, payload, { withCredentials: true });
        toast.success("Menu atualizado com sucesso!");
      } else {
        await axios.post(Hoot() + "nav-menus", payload, { withCredentials: true });
        toast.success("Menu criado com sucesso!");
      }

      ClickAllRefreshButtons();
      onSaved();
    } catch (err) {
      console.error("Erro ao guardar menu:", err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="pb-panel-head">
        <BackButton onClick={onBack} />
        <div className="pb-panel-title">{isEdit ? "Editar menu" : "Novo menu"}</div>
      </div>

      <ErrorBanner error={error} />

      <Form id="menu-form" onSubmit={handleSubmit}>
        <div className="pb-cardlet">
          <Form.Group className="mb-3">
            <Form.Label>
              Nome do menu <span className="pb-req">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Serviços"
              required
            />
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label>Link</Form.Label>
            <Form.Control
              type="text"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="/servicos ou https://..."
              disabled={hasSubmenus}
            />
            {hasSubmenus ? (
              <div className="pb-helper">
                Este menu já tem submenus, por isso não pode ter link próprio — o clique abrirá apenas os
                submenus.
              </div>
            ) : (
              <div className="pb-helper">
                Deixe em branco se pretende adicionar submenus a este menu depois de o criar.
              </div>
            )}
          </Form.Group>
        </div>
      </Form>

      <div className="pb-panel-footer">
        <Button variant="outline-secondary" className="btn-soft" onClick={onBack} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" form="menu-form" variant="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              A guardar...
            </>
          ) : isEdit ? (
            "Guardar alterações"
          ) : (
            "Criar menu"
          )}
        </Button>
      </div>
    </div>
  );
}

/* =========================================================
   PAINEL: Criar/editar SUBMENU
========================================================= */
function SubmenuFormPanel({ menu, submenu, onBack, onSaved }) {
  const isEdit = !!submenu;

  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setName(submenu?.ed_submenu_name || "");
    setLink(submenu?.ed_submenu_link || "");
    setError(null);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submenu]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!name.trim() || !link.trim()) {
      toast.error("Nome e link são obrigatórios para um submenu.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = { name: name.trim(), link: link.trim() };

      if (isEdit) {
        await axios.put(Hoot() + "nav-submenus/" + submenu.ed_submenu_id, payload, { withCredentials: true });
        toast.success("Submenu atualizado com sucesso!");
      } else {
        await axios.post(Hoot() + "nav-menus/" + menu.ed_menu_id + "/submenus", payload, {
          withCredentials: true,
        });
        toast.success("Submenu criado com sucesso!");
      }

      ClickAllRefreshButtons();
      onSaved();
    } catch (err) {
      console.error("Erro ao guardar submenu:", err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="pb-panel-head">
        <BackButton onClick={onBack} />
        <div>
          <div className="pb-panel-title">{isEdit ? "Editar submenu" : "Novo submenu"}</div>
          <div className="pb-panel-sub">{menu?.ed_menu_name}</div>
        </div>
      </div>

      <ErrorBanner error={error} />

      <Form id="submenu-form" onSubmit={handleSubmit}>
        <div className="pb-cardlet">
          <Form.Group className="mb-3">
            <Form.Label>
              Nome do submenu <span className="pb-req">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Consultoria"
              required
            />
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label>
              Link <span className="pb-req">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="/servicos/consultoria"
              required
            />
          </Form.Group>
        </div>
      </Form>

      <div className="pb-panel-footer">
        <Button variant="outline-secondary" className="btn-soft" onClick={onBack} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" form="submenu-form" variant="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              A guardar...
            </>
          ) : isEdit ? (
            "Guardar alterações"
          ) : (
            "Criar submenu"
          )}
        </Button>
      </div>
    </div>
  );
}

/* =========================================================
   COMPONENTE PRINCIPAL: Page Builder
   UM único modal, com sidebar interna (Páginas / Menu de Navegação) que troca
   o painel apresentado (listagem, criação/edição, secções, editor de secção).
   Nada disto abre um <Modal> novo — é tudo dentro do mesmo diálogo.
========================================================= */
export default function PageBuilderModule({ toggle_btn }) {
  const [show, setShow] = useState(false);
  // view.key controla qual painel é mostrado dentro do modal único.
  // Chaves possíveis: pages-list, page-form, page-sections, section-form,
  // menus-list, menu-form, submenu-form.
  const [view, setView] = useState({ key: "pages-list" });

  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState(null);

  const [menus, setMenus] = useState([]);
  const [menusLoading, setMenusLoading] = useState(false);
  const [menusError, setMenusError] = useState(null);

  const goTo = (key, payload = {}) => setView({ key, ...payload });

  const openModal = () => setShow(true);
  const closeModal = () => {
    setShow(false);
    setView({ key: "pages-list" });
  };

  const loadPages = useCallback(async () => {
    setPagesLoading(true);
    setPagesError(null);
    try {
      const res = await axios.get(Hoot() + "pages", { withCredentials: true });
      setPages(res.data || []);
    } catch (err) {
      console.error("Erro ao carregar páginas:", err);
      setPagesError(extractErrorMessage(err));
    } finally {
      setPagesLoading(false);
    }
  }, []);

  const loadMenus = useCallback(async () => {
    setMenusLoading(true);
    setMenusError(null);
    try {
      const res = await axios.get(Hoot() + "nav-menus", { withCredentials: true });
      const list = (res.data || []).sort((a, b) => a.ed_menu_display_order - b.ed_menu_display_order);
      setMenus(list);
    } catch (err) {
      console.error("Erro ao carregar menus:", err);
      setMenusError(extractErrorMessage(err));
    } finally {
      setMenusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      loadPages();
      loadMenus();
    }
  }, [show, loadPages, loadMenus]);

  const activeGroup = view.key.startsWith("menu") || view.key === "submenu-form" ? "menus" : "paginas";

  return (
    <>
      <style>{styles}</style>

      <div onClick={openModal} style={{ display: "inline-block" }}>
        {toggle_btn || (
          <Button variant="success" className="pb-trigger-btn">
            <BsGlobe />
          </Button>
        )}
      </div>

      <Modal show={show} onHide={closeModal} centered className="pb-main-modal" backdrop="static">
        <Modal.Header closeButton>
          <div className="pb-header-left">
            <div className="pb-avatar-wrap">
              <BsGlobe style={{ color: "#16a34a", fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontWeight: 650 }}>Page Builder</div>
              <div className="pb-title-sub">
                Cria, edita e organiza as páginas e a navegação do website sem tocar em código.
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: 0 }}>
          <div className="pb-shell">
            <div className="pb-sidebar">
              <button
                type="button"
                className={`pb-sidebar-item${activeGroup === "paginas" ? " active" : ""}`}
                onClick={() => goTo("pages-list")}
              >
                <BsFileEarmarkText />
                <span>Páginas</span>
              </button>
              <button
                type="button"
                className={`pb-sidebar-item${activeGroup === "menus" ? " active" : ""}`}
                onClick={() => goTo("menus-list")}
              >
                <BsListNested />
                <span>Menu de Navegação</span>
              </button>
            </div>

            <div className="pb-panel">
              {view.key === "pages-list" && (
                <PagesListPanel
                  pages={pages}
                  loading={pagesLoading}
                  error={pagesError}
                  onRetry={loadPages}
                  onCreate={() => goTo("page-form", { editingPage: null })}
                  onEdit={(p) => goTo("page-form", { editingPage: p })}
                  onSections={(p) => goTo("page-sections", { page: p })}
                  onReload={loadPages}
                />
              )}

              {view.key === "page-form" && (
                <PageFormPanel
                  page={view.editingPage}
                  existingPages={pages}
                  onBack={() => goTo("pages-list")}
                  onSaved={() => {
                    loadPages();
                    goTo("pages-list");
                  }}
                />
              )}

              {view.key === "page-sections" && (
                <PageSectionsPanel
                  key={view.page?.ed_page_id}
                  page={view.page}
                  onBack={() => goTo("pages-list")}
                  onCreateSection={() => goTo("section-form", { page: view.page, editingSection: null })}
                  onEditSection={(s) => goTo("section-form", { page: view.page, editingSection: s })}
                />
              )}

              {view.key === "section-form" && (
                <SectionFormPanel
                  key={`${view.page?.ed_page_id}-${view.editingSection?.ed_section_id ?? "new"}`}
                  page={view.page}
                  section={view.editingSection}
                  onBack={() => goTo("page-sections", { page: view.page })}
                  onSaved={() => goTo("page-sections", { page: view.page })}
                />
              )}

              {view.key === "menus-list" && (
                <MenusListPanel
                  menus={menus}
                  loading={menusLoading}
                  error={menusError}
                  onRetry={loadMenus}
                  onReload={loadMenus}
                  onCreateMenu={() => goTo("menu-form", { editingMenu: null })}
                  onEditMenu={(m) => goTo("menu-form", { editingMenu: m })}
                  onCreateSubmenu={(m) => goTo("submenu-form", { parentMenu: m, editingSubmenu: null })}
                  onEditSubmenu={(m, s) => goTo("submenu-form", { parentMenu: m, editingSubmenu: s })}
                />
              )}

              {view.key === "menu-form" && (
                <MenuFormPanel
                  menu={view.editingMenu}
                  onBack={() => goTo("menus-list")}
                  onSaved={() => {
                    loadMenus();
                    goTo("menus-list");
                  }}
                />
              )}

              {view.key === "submenu-form" && (
                <SubmenuFormPanel
                  menu={view.parentMenu}
                  submenu={view.editingSubmenu}
                  onBack={() => goTo("menus-list")}
                  onSaved={() => {
                    loadMenus();
                    goTo("menus-list");
                  }}
                />
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" className="btn-soft" onClick={closeModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}